import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RequestFormActions } from 'site/pages/requests/request-form/request-form';
import { RequestFormViewModel } from 'stores/requests/request-form-store';
import { RequestDto } from 'stores/requests/requests-store';
import RequestCancelButtonTailwind from './buttons/request-cancel-button-tw';
import RequestEditButtonTailwind from './buttons/request-edit-button-tw';
import { History } from 'history';
import RequestRejectButtonTw from './buttons/request-reject-button-tw';
import RequestAcceptButtonTw from './buttons/request-accept-button-tw';
import { isExtendedWorkflow } from 'utils/event-type-utils';
import RequestSendButtonTw from './buttons/request-send-button-tw';
import RequestReopenButtonTw from './buttons/request-reopen-button-tw';
import { ToastComponent } from '../../util/toast-component';

interface BottomToolbarActionsProps {
  request: RequestFormViewModel;
  readOnly: boolean;
  history?: History;
  onClose?: (request?: RequestDto, action?: RequestFormActions) => void;
  onEdit?: () => void;
  onSave?: () => void;
  onErrors?: (errors: any) => void;
  onBeginAccept?: (value: boolean) => void;
  canEdit?: boolean;
  sidebarWidthPx?: number;
}

const BottomToolbarActions: React.FC<BottomToolbarActionsProps> = ({
  request,
  readOnly,
  history,
  onClose,
  onEdit,
  onSave,
  onBeginAccept,
  onErrors,
  canEdit = true,
  sidebarWidthPx = 56
}) => {
  const { t } = useTranslation();

  const [saveDone] = useState<boolean>(false);

  const replaceActionOnClose = (actionName?: RequestFormActions): RequestFormActions => {
    let action: RequestFormActions = actionName;
    if (action === 'close' && saveDone) {
      action = 'save';
    }
    return action;
  };

  const handleOnCloseModal = (request?: RequestDto, actionName?: RequestFormActions) => {
    if (onClose) {
      const action = replaceActionOnClose(actionName);
      onClose(request, action);
    }

    if (history) {
      history.replace('/reload');
      setTimeout(() => {
        history.replace('/requests');
      });
    }
  };

  const changeToEventCreated = (request: RequestDto = null) => {
    if (history && request && request.eventCreatedId && !isExtendedWorkflow(request.eventType.eventTypeCategory)) {
      //history.replace(`/events/${request.eventCreatedId}`);
      history.replace(`/events`);
    } else {
      //this.setState({ loading: false });
      handleOnCloseModal(request);
    }
  };

  const errorHandler = (errors: any) => {
    onErrors(errors);
  };

  const handleShowToastReopened = (request: RequestDto) => {
    ToastComponent({ type: 'success-toast', text: t('Form successfully reopened!') });
    //requestFormStore.change(buildRequestFormViewModel(request)); TODO://llamar a una función que haga esto en el padre.
  };

  return (
    <div
      className="fixed bottom-0 border-t border-gray-300 bg-white px-2 py-2 shadow-lg h-auto min-h-[0px]"
      style={{
        zIndex: 100,
        left: sidebarWidthPx,
        width: `calc(100% - ${sidebarWidthPx}px)`,
        display: 'flex',
        flexDirection: 'column',
        gap: '6px'
      }}
    >
      <div className="flex justify-between gap-1">
        <RequestCancelButtonTailwind
          readOnly={readOnly}
          requestId={request.id}
          isGea={request.isGEA}
          isGma={request.isGMA}
          requestCreatorRoles={request.rolesThatCreateRequests}
          requestStatus={request.status}
          onSuccess={() => handleOnCloseModal(null, 'cancel')}
        />

        <RequestEditButtonTailwind
          requestId={request.id}
          requestStatus={request.status}
          requestCreatorRoles={request.rolesThatCreateRequests}
          isGea={request.isGEA}
          isGma={request.isGMA}
          extendedStatusValidation={request.extendedStatusValidation}
          isOnEditMode={!readOnly}
          onEdit={onEdit}
          onSave={onSave}
        />

        <RequestRejectButtonTw
          readOnly={readOnly}
          requestId={request.id}
          isGea={request.isGEA}
          isGma={request.isGMA}
          extendedStatusValidation={request.extendedStatusValidation}
          requestValidatorRoles={request.rolesThatValidateRequests}
          requestStatus={request.status}
          onSuccess={handleOnCloseModal}
        />

        <RequestAcceptButtonTw
          readOnly={readOnly}
          requestId={request.id}
          onBeginAccept={() => onBeginAccept(true)}
          requestValidatorRoles={request.rolesThatValidateRequests}
          isGea={request.isGEA}
          isGma={request.isGMA}
          extendedStatusValidation={request.extendedStatusValidation}
          requestStatus={request.status}
          onSuccess={changeToEventCreated}
          onError={extraErrors => errorHandler(extraErrors)}
        />

        <RequestSendButtonTw
          readOnly={readOnly}
          requestId={request.id}
          onBeginSend={() => onBeginAccept(true)}
          requestStatus={request.status}
          onSuccess={handleOnCloseModal}
          onError={() => {} /*this.setState({ loading: false })*/}
          requestCreatorRoles={request.rolesThatCreateRequests}
        />

        <RequestReopenButtonTw
          readOnly={readOnly}
          requestId={request.id}
          onBeginReOpen={() => onBeginAccept(true)}
          requestStatus={request.status}
          onSuccess={handleShowToastReopened}
          onError={() => {} /*this.setState({ loading: false })*/}
          requestCreatorRoles={request.rolesThatCreateRequests}
        />

        {/*
        <div className="flex justify-between gap-2">
          <button ... />
          <button ... />
        </div>*/}
      </div>
    </div>
  );
};

export default BottomToolbarActions;
